import React, { Fragment } from 'react'
import LineItemInput from './LineItemInput'
import { Box, Divider, Grid, IconButton, Typography } from '@mui/material'

import {
  FilledInput,
  FormLabel,
  ListItemText,
  makeStyles,
  MenuItem,
  Select
} from '@material-ui/core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useTranslation } from 'react-i18next'
import { lineItemFields, rateTypeOptions } from '../../../lib/Constants'

const useStyles = makeStyles(theme => ({
  lineItemTitle: {
    fontSize: '12px',
    letterSpacing: '1px',
    fontFamily: 'Rubik',
    fontWeight: '600',
    lineHeight: '14px',
    marginBottom: '5px',
    paddingLeft: '5px'
  },
  singleLineItemContainer: {
    marginBottom: '20px'
  },
  fieldTitle: {
    marginBottom: '5px',
    marginLeft: '5px',
    color: theme.colors.text,
    fontSize: '12px',
    fontWeight: '700'
  },
  dividerContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  divider: {
    backgroundColor: theme.colors.grey,
    width: '0.2px'
  },
  iconStyle: {
    fontSize: '14px',
    color: theme.colors.grey
  },
  description: {
    '& .MuiInputBase-root': {
      height: '50px', // Change height here
      backgroundColor: theme.colors.grey_2, // Change background color here
      fontSize: '14px'
    }
  },
  titleSpace: {
    height: '10px'
  },
  placeholder: {
    '& .MuiFilledInput-input': {
      fontSize: '14px',
      color: `${theme.colors.placeholder} !important`,
      lineHeight: '20px'
    }
  },
  selectPlaceholder: {
    fontSize: '14px',
    color: `${theme.colors.placeholder} !important`
  },
  selectedItem: {
    '& .MuiFilledInput-input': {
      fontSize: '14px',
      color: `${theme.colors.basicDisabledButtonColor} !important`,
      lineHeight: '20px'
    }
  },
  select: {
    fontSize: '16px',
    width: '100%',
    borderRadius: '8px'
  },
  menuItem: {
    '& .MuiListItemText-primary': {
      fontSize: '14px',
      paddingBottom: '5px',
      maxWidth: '200px',
      textWrap: 'wrap'
    }
  },
  menuItePlaceholder: {
    '& .MuiListItemText-primary': {
      fontSize: '14px',
      paddingBottom: '5px',
      maxWidth: '200px',
      textWrap: 'wrap',
      color: `${theme.colors.placeholder} !important`
    }
  },
  root: {
    '& .MuiFilledInput-input': {
      border: `1px solid ${theme.colors.signInButton.background}`,
      color: `${theme.colors.iconBlue} !important`,
      backgroundColor: theme.colors.signInButton.background,
      cursor: 'pointer',
      paddingTop: '15px',
      paddingBottom: '15px',
      borderRadius: '8px'
    },
    '& .MuiFilledInput-root': {
      backgroundColor: theme.colors.signInButton.background,
      cursor: 'pointer'
    },
    '& .MuiFilledInput-underline:before': {
      border: 'transparent'
    },
    '& .MuiFilledInput-underline:after': {
      border: 'transparent'
    },
    '& .MuiFilledInput-multiline': {
      paddingTop: '15px',
      paddingBottom: '15px'
    },
    marginBottom: '12px'
  },
  mandatory: {
    color: theme.colors.errorColor
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flex: 1
  }
}))

function SingleLineItem({
  itemSettings,
  rateOptions,
  item,
  index,
  onChange,
  onRemove,
  disabled,
  hideRightAction,
  mandatory
}) {
  const enableRate = itemSettings?.rate !== rateTypeOptions.DISABLE
  const enableLaborType = itemSettings?.labor_type
  const enableDescription = itemSettings?.description
  const enableTax = itemSettings?.tax

  const laborTypeOptions = itemSettings?.labor_type_options ?? []

  const { t } = useTranslation()
  const classes = useStyles()

  function handleLineItemChange(index, value) {
    onChange(index, lineItemFields.LABOR_TYPE, value)
  }

  function handleRateChange(index, value) {
    onChange(index, lineItemFields.RATE, value)
  }

  const SelectedValue = ({ selectedItem }) => {
    if (selectedItem) {
      return (
        <div key={selectedItem} className={classes.menuItem}>
          {selectedItem}
        </div>
      )
    } else {
      return (
        <div className={classes.selectPlaceholder}>
          {t('general.labels.select')}
        </div>
      )
    }
  }

  const RightAction = () => {
    if (!hideRightAction) {
      return (
        <IconButton
          aria-label="delete"
          onClick={() => onRemove(index)}
          disabled={disabled}
        >
          <FontAwesomeIcon
            icon="fa-sharp fa-regular fa-circle-xmark"
            className={classes.iconStyle}
          />
        </IconButton>
      )
    }
    return null
  }
  return (
    <Box className={classes.singleLineItemContainer}>
      <Box className={classes.titleContainer}>
        <Typography
          // note: these styles won't work as a class for some reason
          sx={{
            fontSize: '12px',
            letterSpacing: '1px',
            fontFamily: 'Rubik',
            fontWeight: '600',
            lineHeight: '14px',
            marginBottom: '5px'
          }}
        >
          {t('work_orders.line_item.line_item')} {index + 1}
        </Typography>
        {!disabled && <RightAction />}
      </Box>
      {enableLaborType && (
        <Fragment>
          <FormLabel
            component="legend"
            className={`${classes.fieldTitle} ${
              !disabled && mandatory ? classes.mandatory : ''
            }`}
          >
            {t('work_orders.line_item.labor_type')}
          </FormLabel>
          <Select
            disabled={disabled}
            displayEmpty
            value={item?.labor_type}
            onChange={e => handleLineItemChange(index, e.target.value)}
            input={
              <FilledInput
                className={`${classes.root} ${classes.select}  ${classes.placeholder}`}
                disableUnderline
              />
            }
            renderValue={selected => {
              return <SelectedValue selectedItem={selected} />
            }}
            inputProps={{
              'aria-label': 'Select',
              placeholder: t('general.labels.select')
            }}
            MenuProps={{
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left'
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left'
              },
              getContentAnchorEl: null
            }}
          >
            {laborTypeOptions.map(name => (
              <MenuItem key={name} value={name}>
                <ListItemText className={classes.menuItem} primary={name} />
                {item?.labor_type === name && (
                  <FontAwesomeIcon
                    icon={['fas', 'check']}
                    className={classes.checkIcon}
                  />
                )}
              </MenuItem>
            ))}
          </Select>
        </Fragment>
      )}
      {enableRate && (
        <Fragment>
          <FormLabel
            component="legend"
            className={`${classes.fieldTitle} ${
              !disabled && mandatory ? classes.mandatory : ''
            }`}
          >
            {t('work_orders.line_item.rate')}
          </FormLabel>
          <Select
            disabled={disabled}
            displayEmpty
            value={item?.rate}
            onChange={e => handleRateChange(index, e.target.value)}
            input={
              <FilledInput
                className={`${classes.root} ${classes.select}  ${classes.placeholder}`}
                disableUnderline
              />
            }
            renderValue={selected => {
              return <SelectedValue selectedItem={selected} />
            }}
            inputProps={{
              'aria-label': 'Select',
              placeholder: t('general.labels.select')
            }}
            MenuProps={{
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left'
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left'
              },
              getContentAnchorEl: null
            }}
          >
            {rateOptions.map(name => (
              <MenuItem key={name} value={name}>
                <ListItemText className={classes.menuItem} primary={name} />
                {item?.rate === name && (
                  <FontAwesomeIcon
                    icon={['fas', 'check']}
                    className={classes.checkIcon}
                  />
                )}
              </MenuItem>
            ))}
          </Select>
        </Fragment>
      )}
      {enableDescription && (
        <LineItemInput
          textAreaAutosize={true}
          customClasses={classes.description}
          multiline={true}
          disabled={disabled}
          placeholder={t('work_orders.line_item.enter_additional_description')}
          label={t('work_orders.line_item.additional_description')}
          onChange={e =>
            onChange(index, lineItemFields.DESCRIPTION, e.target.value)
          }
          value={item?.description}
        />
      )}
      {/* items in grid */}

      <Grid container rowSpacing={1}>
        <Grid item xs={6}>
          <LineItemInput
            disabled={disabled}
            placeholder={'$000.00'}
            label={`${t('work_orders.line_item.amount')} #`}
            onChange={e =>
              onChange(index, lineItemFields.AMOUNT, e.target.value)
            }
            value={item.amount}
          />
        </Grid>
        <Grid item xs={1} className={classes.dividerContainer}>
          <Divider orientation="vertical" className={classes.divider} />
        </Grid>
        <Grid item xs>
          <LineItemInput
            disabled={disabled}
            placeholder={'00'}
            label={t('work_orders.line_item.qty')}
            onChange={e =>
              onChange(index, lineItemFields.QUANTITY, e.target.value)
            }
            value={item?.quantity}
          />
        </Grid>
        {enableTax && (
          <Fragment>
            <Grid item xs={6}>
              <LineItemInput
                disabled={disabled}
                placeholder={'00.00%'}
                label={`${t('work_orders.line_item.tax')} (%)`}
                onChange={e =>
                  onChange(index, lineItemFields.TAX, e.target.value)
                }
                value={item?.tax}
              />
            </Grid>
            <Grid item xs={1} className={classes.dividerContainer}>
              <Divider orientation="vertical" className={classes.divider} />
            </Grid>
          </Fragment>
        )}
        <Grid item xs={enableTax ? true : 12}>
          <LineItemInput
            disabled={true} // always disabled
            placeholder={'$000.00'}
            label={
              enableTax
                ? t('work_orders.line_item.total_price')
                : t('work_orders.line_item.total_amount')
            }
            onChange={e =>
              onChange(index, lineItemFields.TOTAL, e.target.value)
            }
            value={item?.total}
          />
        </Grid>
      </Grid>
    </Box>
  )
}

export default SingleLineItem
